/**
 * DriverMap — live driver position map (DISPATCH-02).
 *
 * Phase 9 / Plan 09-05 — replaces the Plan 09-04 placeholder stub with the real
 * Mapbox dark-v11 implementation. Shows one pin per driver at their latest-known
 * synced GPS position, polled every 30s. Offline/stale drivers are SHOWN at
 * last-known position with a dashed-warning border + "as of HH:MM" label —
 * they are NEVER hidden (D-03 honesty contract).
 *
 * CSS imports for mapbox-gl and mapbox-gl-draw are in main.tsx — NOT here.
 * (RESEARCH Pitfall 1 — CSS before components in entry point only.)
 *
 * Spec: UI-SPEC §4 (Live Driver Map); DISPATCH-02; DEC-004; D-03.
 */

import * as React from 'react';
import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Maximize2, Minimize2, Crosshair, Layers, Users } from 'lucide-react';
import { useSharedMap } from '../map/MapProvider.js';
import { useDriverPositions } from './use-driver-positions.js';
import type { DriverPosition } from '@lodex-pro/shared';
import { useUserTimezone } from '../../lib/use-user-timezone.js';
import { formatStaleness, formatTimestamp, isStale } from '../../lib/timestamp.js';
import { statusFullLabel } from '../jobs/job-status.js';
import { driverInitials } from './driver-pin.js';
import {
  driverAvailability,
  IN_PROGRESS_STATUSES,
  type DriverAvailability,
} from '../board/board-utils.js';
import {
  LAYER_COLORS,
  SITE_CIRCLE_LAYERS,
  anyLayerOn,
  applyLayerVisibility,
  buildLayerData,
  ensureOpsLayers,
  removeOpsLayers,
  setOpsLayerData,
  type LayerData,
  type MapLayerPrefs,
} from './map-layers.js';
import { useMapLayerPrefs } from './use-map-layer-prefs.js';
import { DriversPanel } from './DriversPanel.js';
import {
  NO_DRIVER_FILTER,
  overlayPositions,
  pinDimmed,
  pruneToPresent,
  toggled,
  type DriverOverlayState,
} from './driver-overlay-filter.js';
import { formatNumber } from '@lodex-pro/shared/number-format';

// #53 PR 3 — a pin outside an active Focus. On the marker's root, so it multiplies with
// the stale dim on the inner pin: a stale, unfocused truck is fainter still, never brighter.
const UNFOCUSED_PIN_OPACITY = '0.3';

// #53 / DEC-360 — the three optional layers, in the order the Layers menu lists them.
const LAYER_MENU: ReadonlyArray<{ key: keyof MapLayerPrefs; label: string; color: string }> = [
  { key: 'pickups', label: 'Pickup sites', color: LAYER_COLORS.pickup },
  { key: 'disposals', label: 'Disposal sites', color: LAYER_COLORS.disposal },
  { key: 'routes', label: 'Route lines', color: LAYER_COLORS.route },
];

// ---------------------------------------------------------------------------
// Mapbox token guard
// ---------------------------------------------------------------------------

const MAPBOX_TOKEN = import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined;

// Quick 260614-s5w (#7) — selectable base styles so the dispatcher can switch
// off the dark style to identify a location (satellite/streets).
const MAP_STYLES: ReadonlyArray<{ key: string; label: string; url: string }> = [
  { key: 'dark', label: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
  { key: 'streets', label: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' },
  { key: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
];

// ---------------------------------------------------------------------------
// Availability colors + marker element builders
// ---------------------------------------------------------------------------

/**
 * Quick 260617-map (#2) — driver pins are colored by AVAILABILITY (matching the
 * dispatch-board driver cards): green=Available, amber=Busy, red=Offline. Replaces
 * the old per-driver hash palette so the map and board read consistently.
 */
const AVAILABILITY_COLOR: Record<DriverAvailability, string> = {
  available: 'var(--color-success)',
  busy: 'var(--color-warning)',
  offline: 'var(--color-danger)',
};

/** Resolve a driver's availability from a positions row (reuses the board logic). */
function availabilityOf(pos: DriverPosition): DriverAvailability {
  const hasActiveJob = pos.jobStatusCode != null && IN_PROGRESS_STATUSES.has(pos.jobStatusCode);
  return driverAvailability(hasActiveJob, pos.positionAt);
}

/**
 * Build a 32×32px circular DOM element for the driver pin. Fill = availability color
 * (#2). Stale positions (>10 min) get an "as of HH:MM" freshness label below the
 * pin (NOT a warning border — amber now means Busy). Opacity encodes age
 * (Quick 260619-map #5): >2h → 40% (matches the board's 2h "offline" threshold);
 * >10min but ≤2h → 70%. Pins >24h are excluded server-side, so never reach here.
 */
function buildMarkerElement(
  name: string,
  color: string,
  stale: boolean,
  veryStale: boolean,
  positionAt: string,
  tz: string,
): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer;';

  const pin = document.createElement('div');
  const initials = driverInitials(name);

  pin.style.cssText = [
    'width: 32px',
    'height: 32px',
    'border-radius: 50%',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    `background-color: ${color}`,
    'border: 2px solid var(--color-bg-primary)',
    'box-shadow: 0 0 0 1px rgba(0,0,0,0.35)',
    'font-family: var(--font-sans)',
    'font-size: 12px',
    'font-weight: 600',
    'color: var(--color-on-status)',
    'user-select: none',
  ].join('; ');

  // #5 — older than 2h dims to 40%; otherwise stale (>10min) dims to 70%.
  if (veryStale) pin.style.opacity = '0.4';
  else if (stale) pin.style.opacity = '0.7';

  pin.textContent = initials;
  wrapper.appendChild(pin);

  if (stale) {
    const label = document.createElement('span');
    label.style.cssText = [
      'font-family: var(--font-mono, JetBrains Mono, monospace)',
      'font-size: 12px',
      'color: var(--color-text-secondary)',
      'margin-top: 2px',
      'white-space: nowrap',
      'pointer-events: none',
    ].join('; ');
    label.textContent = formatStaleness(positionAt, tz);
    wrapper.appendChild(label);
  }

  return wrapper;
}

/** Escape user-controlled text before injecting into the popup HTML (setHTML). */
function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/**
 * Build the driver-pin callout HTML (DISPATCH-02). Shows driver name, current job
 * status (+ haul id), site/lease name, and last-known-position time — all from the
 * GET /v1/drivers/positions row. Previously the popup only carried a staleness
 * string via setText and rendered near-invisible (light text on Mapbox's default
 * white popup), so the callout looked blank. Styled via `.driver-pin-popup` in
 * styles.css for the dark theme.
 */
function buildCalloutHtml(pos: DriverPosition, tz: string, focused = false): string {
  const name = escapeHtml(pos.driverName || 'Driver');
  const truck = pos.truckNumber ? escapeHtml(pos.truckNumber) : null;
  const operator = pos.operatorName ? escapeHtml(pos.operatorName) : '—';
  const status = pos.jobStatusCode
    ? escapeHtml(statusFullLabel(pos.jobStatusCode))
    : 'No active job';
  const statusValue = pos.haulId ? `${status} · ${escapeHtml(pos.haulId)}` : status;
  const site = pos.siteName ? escapeHtml(pos.siteName) : '—';
  const lastSync = escapeHtml(formatTimestamp(pos.positionAt, tz));
  const row = (k: string, v: string): string =>
    `<div class="driver-callout-row"><span class="k">${k}</span><span class="v">${v}</span></div>`;
  const rows = [`<div class="driver-callout-name">${name}</div>`];
  // #1 — driver name, truck (if assigned), operator, status, site, last sync.
  //
  // OPEN-027 — SHIFT is stated explicitly, and stated FIRST. A blank truck row
  // used to mean either "no shift" or "on shift, truck not set"; a dispatcher
  // could not tell, so a driver working a full day with no shift row looked
  // normal until the tickets came back blank. Truck is shown underneath because
  // a truck with no shift behind it is the combination worth noticing.
  // Plain text, no markup: every other value in this callout is escapeHtml'd, and
  // mixing raw HTML here would invite a future edit to pass unescaped user data.
  rows.push(row('Shift', pos.onShift ? 'On shift' : 'NOT ON SHIFT'));
  if (truck) rows.push(row('Truck', truck));
  else if (pos.onShift) rows.push(row('Truck', 'not set'));
  rows.push(row('Operator', operator));
  rows.push(row('Status', statusValue));
  rows.push(row('Site', site));
  rows.push(row('Last sync', lastSync));
  // #53 follow-up — the pin is clickable now; say what a click does (the callout is
  // hover-only, so the hint lives here rather than as a button nobody can reach).
  rows.push(
    `<div class="driver-callout-row"><span class="v">${
      focused ? 'Focused · click the truck to stop' : 'Click the truck to focus'
    }</span></div>`,
  );
  return `<div class="driver-callout">${rows.join('')}</div>`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface DriverMapProps {
  /**
   * Whether the Map View tab is currently visible. Quick 260619-map (#2): the map
   * is kept mounted (display-toggled) across tab switches so the shared Mapbox
   * instance never reloads (DEC-057). When false, position polling pauses; when it
   * flips true the canvas is resized (a display:none container measures 0×0).
   * Defaults to true so existing render sites / tests behave unchanged.
   */
  readonly active?: boolean;
}

export function DriverMap({ active = true }: DriverMapProps): React.JSX.Element {
  // Token guard — if absent, render the UI-SPEC error copy (no map init, no crash)
  if (!MAPBOX_TOKEN) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'var(--color-bg-secondary)',
          color: 'var(--color-text-secondary)',
          fontSize: '14px',
          fontFamily: 'var(--font-sans, system-ui, sans-serif)',
        }}
      >
        <p>Map unavailable — check your Mapbox token or network connection.</p>
      </div>
    );
  }

  return <DriverMapInner active={active} />;
}

/**
 * Inner component — only rendered when MAPBOX_TOKEN is defined.
 * Separating the token guard into the outer shell avoids Hook-order issues
 * (rules of hooks: we cannot conditionally call useEffect in the same render).
 */
const mapBtnStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-pill)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

function DriverMapInner({ active = true }: DriverMapProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Track active markers by driverId so we can update or remove them on each poll.
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());

  // Wave 2 (#1) — the map is the shared, session-lived instance, not a per-mount one.
  const { acquire, release } = useSharedMap();
  // Quick 260619-map (#2) — pause the 30s position poll while the Map View tab is
  // hidden (the map stays mounted but display:none).
  const { data, isFetching } = useDriverPositions(active);
  const tz = useUserTimezone();

  // Quick 260614-s5w — UI state for the new map controls (#5/#7).
  const [styleKey, setStyleKey] = useState<string>('dark');
  const [expanded, setExpanded] = useState<boolean>(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  // Skip the first setStyle effect run — the init effect already applied the style.
  const styleInitedRef = useRef(false);

  // Memoize to stabilize the array reference across renders (react-hooks/exhaustive-deps)
  const positions = useMemo(() => data?.positions ?? [], [data]);

  // #53 / DEC-360 — optional layers. All off by default; remembered per user.
  const { prefs: layerPrefs, setLayer } = useMapLayerPrefs();
  const [layersOpen, setLayersOpen] = useState<boolean>(false);
  // #53 PR 3 — per-driver eye + Focus. Page state only: resets on reload (C4).
  const [driversOpen, setDriversOpen] = useState<boolean>(false);
  const [driverFilter, setDriverFilter] = useState<DriverOverlayState>(NO_DRIVER_FILTER);
  // Pruned to the drivers on the map, so a Focus on someone who has dropped off ends.
  const effectiveFilter = useMemo(
    () => pruneToPresent(positions, driverFilter),
    [positions, driverFilter],
  );
  const overlayPos = useMemo(
    () => overlayPositions(positions, effectiveFilter),
    [positions, effectiveFilter],
  );
  // Rebuilt on each 30 s poll; staleness is judged at that moment, like the pins.
  const layerData = useMemo(() => buildLayerData(overlayPos, Date.now()), [overlayPos]);
  // The `style.load` handler is registered once, so it reads the latest values via refs.
  const layerPrefsRef = useRef<MapLayerPrefs>(layerPrefs);
  const layerDataRef = useRef<LayerData>(layerData);
  layerPrefsRef.current = layerPrefs;
  layerDataRef.current = layerData;

  // Wave 2 (#1) — attach the SHARED map into this surface's slot (never create a
  // new one). On unmount, tear down ONLY this surface's markers + listeners and
  // park the map back in the provider holder (it is never .remove()d here).
  useEffect(() => {
    if (!containerRef.current) return;
    const map = acquire(containerRef.current);
    if (!map) return;
    mapRef.current = map;

    // Ensure the Operations base style (the geofence surface leaves it satellite).
    const initialStyle =
      MAP_STYLES.find((s) => s.key === styleKey)?.url ?? 'mapbox://styles/mapbox/dark-v11';
    map.setStyle(initialStyle);

    // Quick 260614-s5w (#7) — live coordinate readout. Named handlers so we can
    // off() them precisely on unmount (the shared map outlives this component).
    const onMouseMove = (e: mapboxgl.MapMouseEvent): void => {
      setCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };
    const onMouseOut = (): void => {
      setCoords(null);
    };
    map.on('mousemove', onMouseMove);
    map.on('mouseout', onMouseOut);

    // #53 / DEC-360 — the optional layers. setStyle() (here and in the style switcher)
    // wipes every added source and layer, so they are (re)added on each style load —
    // the same pattern as the Geofence editor. Idempotent.
    const onStyleReady = (): void => {
      ensureOpsLayers(map, layerPrefsRef.current);
      setOpsLayerData(map, layerDataRef.current);
    };
    map.on('style.load', onStyleReady);
    if (map.isStyleLoaded()) onStyleReady();

    // Click a site → which trucks are bound for it (the marker shows only a count).
    let sitePopup: mapboxgl.Popup | null = null;
    const onSiteClick = (e: mapboxgl.MapLayerMouseEvent): void => {
      const f = e.features?.[0];
      const props = f?.properties;
      if (f === undefined || props == null || f.geometry.type !== 'Point') return;
      const [lng, lat] = f.geometry.coordinates as [number, number];
      const kind = props['kind'] === 'DISPOSAL' ? 'Disposal' : 'Pickup';
      sitePopup?.remove();
      sitePopup = new mapboxgl.Popup({
        closeButton: true,
        className: 'driver-pin-popup',
        offset: 12,
      })
        .setLngLat([lng, lat])
        .setHTML(
          `<div style="font-weight:600">${escapeHtml(String(props['name'] ?? ''))}</div>` +
            `<div>${kind} · ${escapeHtml(String(props['drivers'] ?? ''))}</div>`,
        )
        .addTo(map);
    };
    const onSiteEnter = (): void => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onSiteLeave = (): void => {
      map.getCanvas().style.cursor = '';
    };
    for (const id of SITE_CIRCLE_LAYERS) {
      map.on('click', id, onSiteClick);
      map.on('mouseenter', id, onSiteEnter);
      map.on('mouseleave', id, onSiteLeave);
    }

    window.setTimeout(() => {
      map.resize();
    }, 0);

    // Quick 260616-rsz (#1) — keep the Mapbox canvas filling its panel when the
    // panel resizes (browser window resize, split-pane drag, panel collapse/expand).
    // A ResizeObserver on this surface's container fires map.resize() on any size
    // change — more reliable than listening for the global 'resize' event, which
    // does not fire for pane drags or programmatic layout changes.
    const observedContainer = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && observedContainer !== null) {
      resizeObserver = new ResizeObserver(() => {
        mapRef.current?.resize();
      });
      resizeObserver.observe(observedContainer);
    }

    const markersSnapshot = markersRef.current;
    return () => {
      markersSnapshot.forEach((marker) => {
        marker.remove();
      });
      markersSnapshot.clear();
      resizeObserver?.disconnect();
      map.off('mousemove', onMouseMove);
      map.off('mouseout', onMouseOut);
      // #53 — the map is SHARED (the Geofence editor uses it next): take our layers
      // and listeners with us (MapProvider's contract).
      map.off('style.load', onStyleReady);
      for (const id of SITE_CIRCLE_LAYERS) {
        map.off('click', id, onSiteClick);
        map.off('mouseenter', id, onSiteEnter);
        map.off('mouseleave', id, onSiteLeave);
      }
      sitePopup?.remove();
      removeOpsLayers(map);
      mapRef.current = null;
      release();
    };
    // styleKey is read only for the INITIAL style; later changes are handled by
    // the setStyle effect below, so it is intentionally not a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquire, release]);

  // Quick 260614-s5w (#7) — apply base-style changes. DOM markers persist across
  // setStyle (they are not style-bound), so pins survive the swap.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!styleInitedRef.current) {
      styleInitedRef.current = true;
      return; // init effect already applied the initial style
    }
    const url = MAP_STYLES.find((s) => s.key === styleKey)?.url;
    if (url) map.setStyle(url);
  }, [styleKey]);

  // Quick 260614-s5w (#5) — Mapbox must recompute canvas size after the
  // container changes between split and full-screen.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Defer to after the DOM reflow so the new container size is measured.
    const id = window.setTimeout(() => {
      map.resize();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [expanded]);

  // Quick 260619-map (#2) — the map container is display:none while the Map View
  // tab is inactive (it is NEVER unmounted — DEC-057). A hidden container measures
  // 0×0, so Mapbox must recompute its canvas when the tab becomes active again.
  useEffect(() => {
    if (!active) return;
    const map = mapRef.current;
    if (!map) return;
    const id = window.setTimeout(() => {
      map.resize();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [active]);

  // Update driver markers whenever positions or timezone changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const currentDriverIds = new Set(positions.map((p) => p.driverId));

    // Remove markers for drivers no longer in the feed
    markersRef.current.forEach((marker, driverId) => {
      if (!currentDriverIds.has(driverId)) {
        marker.remove();
        markersRef.current.delete(driverId);
      }
    });

    // Add or update markers
    positions.forEach((pos) => {
      const stale = isStale(pos.positionAt);
      // #5 — 2h threshold (120 min) drives the 40%-opacity dim, matching the
      // dispatch board's "offline" cutoff.
      const veryStale = isStale(pos.positionAt, 120);
      const color = AVAILABILITY_COLOR[availabilityOf(pos)];
      const el = buildMarkerElement(pos.driverName, color, stale, veryStale, pos.positionAt, tz);
      // #53 PR 3 — Focus dims the other trucks; it never removes a pin (C2/C3).
      if (pinDimmed(pos.driverId, effectiveFilter)) el.style.opacity = UNFOCUSED_PIN_OPACITY;

      const existingMarker = markersRef.current.get(pos.driverId);
      if (existingMarker) {
        // Update position and replace element
        existingMarker.remove();
      }

      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'driver-pin-popup',
        offset: 16,
      }).setHTML(buildCalloutHtml(pos, tz, effectiveFilter.focused.has(pos.driverId)));

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pos.lng, pos.lat])
        .setPopup(popup)
        .addTo(map);

      // Show tooltip on hover
      el.addEventListener('mouseenter', () => {
        marker.togglePopup();
      });
      el.addEventListener('mouseleave', () => {
        if (marker.getPopup()?.isOpen()) {
          marker.togglePopup();
        }
      });

      // #53 follow-up (Corbin) — click a truck to Focus it, the same toggle as the
      // Drivers panel's Focus button (they share one state). The callout stays on
      // hover. stopPropagation keeps the click from reaching the map, where Mapbox's
      // Marker would otherwise toggle the popup (`_onMapClick`) — which used to close
      // the hovered callout on every click.
      const focused = effectiveFilter.focused.has(pos.driverId);
      el.setAttribute('role', 'button');
      el.tabIndex = 0;
      el.setAttribute('aria-pressed', String(focused));
      el.setAttribute(
        'aria-label',
        `${focused ? 'Stop focusing' : 'Focus on'} ${pos.driverName || 'driver'}`,
      );
      const toggleFocus = (): void => {
        setDriverFilter((s) => ({ ...s, focused: toggled(s.focused, pos.driverId) }));
      };
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleFocus();
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleFocus();
        }
      });

      markersRef.current.set(pos.driverId, marker);
    });
  }, [positions, tz, effectiveFilter]);

  // #53 — push each poll's layer data (a no-op until the style has loaded; the
  // style.load handler then fills it from the ref).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    setOpsLayerData(map, layerData);
  }, [layerData]);

  // #53 — flipping a switch is a visibility change, never a rebuild.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyLayerVisibility(map, layerPrefs);
  }, [layerPrefs]);

  // "Fit all drivers" handler
  const handleFitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map || positions.length === 0) return;

    if (positions.length === 1) {
      const p = positions[0];
      if (p) {
        map.flyTo({ center: [p.lng, p.lat], zoom: 12 });
      }
      return;
    }

    const lngs = positions.map((p) => p.lng);
    const lats = positions.map((p) => p.lat);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 60, maxZoom: 14 },
    );
  }, [positions]);

  const wrapperStyle: React.CSSProperties = expanded
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'var(--color-bg-primary)',
      }
    : { position: 'relative', width: '100%', height: '100%' };

  return (
    <div style={wrapperStyle}>
      {/* Map container */}
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%' }}
        data-testid="mapbox-container"
      />

      {/* "Refreshing..." text indicator — shown during isFetching (not isLoading) */}
      {isFetching && (
        <div
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            fontSize: '12px',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-secondary)',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          Refreshing...
        </div>
      )}

      {/* Control cluster — top-right overlay (#5/#7) */}
      <div
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '6px',
          zIndex: 10,
        }}
      >
        {/* Base-style switcher (#7) */}
        <div
          role="group"
          aria-label="Map style"
          style={{
            display: 'flex',
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}
        >
          {MAP_STYLES.map((s) => {
            const active = s.key === styleKey;
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={active}
                onClick={(): void => {
                  setStyleKey(s.key);
                }}
                style={{
                  padding: '5px 8px',
                  fontSize: '11px',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: active ? 'var(--color-accent-blue)' : 'transparent',
                  color: active ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          {/* Expand / collapse full screen (#5) */}
          <button
            type="button"
            aria-label={expanded ? 'Collapse map' : 'Expand map'}
            aria-pressed={expanded}
            onClick={(): void => {
              setExpanded((v) => !v);
            }}
            style={mapBtnStyle}
          >
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>

          {/* Fit all drivers (distinct icon from expand to avoid confusion) */}
          <button
            type="button"
            aria-label="Fit all drivers"
            onClick={handleFitAll}
            style={mapBtnStyle}
          >
            <Crosshair size={16} />
          </button>

          {/* #53 PR 3 — the Drivers panel (closed by default): per-driver eye + Focus. */}
          <button
            type="button"
            aria-label="Drivers"
            aria-expanded={driversOpen}
            onClick={(): void => {
              setDriversOpen((v) => !v);
            }}
            style={{ ...mapBtnStyle, position: 'relative' }}
          >
            <Users size={16} />
            {/* A hide or Focus outlives closing the panel; the dot keeps it visible. */}
            {effectiveFilter.hidden.size + effectiveFilter.focused.size > 0 ? (
              <span
                data-testid="map-drivers-filter-dot"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '3px',
                  right: '3px',
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-accent-blue)',
                }}
              />
            ) : null}
          </button>

          {/* #53 / DEC-360 — optional layers. The dot says "something extra is on". */}
          <button
            type="button"
            aria-label="Map layers"
            aria-expanded={layersOpen}
            aria-controls="map-layers-menu"
            onClick={(): void => {
              setLayersOpen((v) => !v);
            }}
            style={{ ...mapBtnStyle, position: 'relative' }}
          >
            <Layers size={16} />
            {anyLayerOn(layerPrefs) ? (
              <span
                data-testid="map-layers-on-dot"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '3px',
                  right: '3px',
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-accent-blue)',
                }}
              />
            ) : null}
          </button>
        </div>

        {layersOpen ? (
          <div
            id="map-layers-menu"
            role="group"
            aria-label="Map layers"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              padding: '8px 10px',
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-primary)',
            }}
          >
            {LAYER_MENU.map((l) => (
              <label
                key={l.key}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  role="switch"
                  checked={layerPrefs[l.key]}
                  onChange={(e): void => {
                    setLayer(l.key, e.target.checked);
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: l.color,
                  }}
                />
                {l.label}
              </label>
            ))}
          </div>
        ) : null}
      </div>

      {driversOpen ? (
        <DriversPanel
          positions={positions}
          state={effectiveFilter}
          layersOn={anyLayerOn(layerPrefs)}
          colorOf={(p): string => AVAILABILITY_COLOR[availabilityOf(p)]}
          onToggleHidden={(id): void => {
            setDriverFilter((s) => ({ ...s, hidden: toggled(s.hidden, id) }));
          }}
          onToggleFocus={(id): void => {
            setDriverFilter((s) => ({ ...s, focused: toggled(s.focused, id) }));
          }}
          onShowAll={(): void => {
            setDriverFilter(NO_DRIVER_FILTER);
          }}
          onClose={(): void => {
            setDriversOpen(false);
          }}
        />
      ) : null}

      {/* Live coordinate readout — bottom-left (#7) */}
      {coords && (
        <div
          style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            padding: '3px 8px',
            fontSize: '12px',
            fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
            color: 'var(--color-text-secondary)',
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '4px',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          {formatNumber(coords.lat, { decimals: 5 })}, {formatNumber(coords.lng, { decimals: 5 })}
        </div>
      )}

      {/* #2 — color legend. Collapsed chip; the body reveals on hover (CSS), so it
          takes no permanent space — same pattern as the board's hover tooltips. */}
      <div
        className="map-legend"
        style={{ position: 'absolute', bottom: '8px', right: '8px', zIndex: 10 }}
      >
        <button type="button" className="map-legend-toggle" aria-label="Map color legend">
          Legend
        </button>
        <div className="map-legend-body" role="tooltip">
          <div className="map-legend-title">Driver</div>
          <div className="map-legend-item">
            <span className="map-legend-dot" style={{ background: 'var(--color-success)' }} />
            Available
          </div>
          <div className="map-legend-item">
            <span className="map-legend-dot" style={{ background: 'var(--color-warning)' }} />
            Busy
          </div>
          <div className="map-legend-item">
            <span className="map-legend-dot" style={{ background: 'var(--color-danger)' }} />
            Offline
          </div>
          {/* #53 — DEC-069 removed the old dots partly because nothing explained them:
              every layer that is on gets a legend row. */}
          {anyLayerOn(layerPrefs) ? (
            <>
              <div className="map-legend-title">Layers</div>
              {LAYER_MENU.filter((l) => layerPrefs[l.key]).map((l) => (
                <div key={l.key} className="map-legend-item">
                  <span className="map-legend-dot" style={{ background: l.color }} />
                  {l.label}
                </div>
              ))}
            </>
          ) : null}
        </div>
      </div>

      {/* Empty state — no positions and not loading */}
      {positions.length === 0 && !isFetching && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <p
            style={{
              fontSize: '12px',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-secondary)',
            }}
          >
            No driver positions available
          </p>
        </div>
      )}
    </div>
  );
}
